import { useObservable, useObservableState } from 'observable-hooks';
import { useApolloClient } from '@apollo/client';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    map,
    of,
    startWith,
    switchMap,
} from 'rxjs';
import type { ContactFieldsFragment, ContactsQuery, ContactsQueryVariables } from '@/graphql/types';
import { ContactFieldsFragmentDoc, ContactsDocument } from '@/graphql/types';
import { useFragment as getFragmentData } from '@/graphql';
import warning from '../../../../../utils/logger';
import { fromObservableQuery } from '../../../../../utils/rxjs';
import { extractNodes } from '../../../../../utils/relay';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_MIN_QUERY_LENGTH = 1;

interface ContactAutocompleteResults {
    contacts: ContactFieldsFragment[];
    loading: boolean;
}

export function useContactAutocompleteResults(value: string): ContactAutocompleteResults {
    const client = useApolloClient();
    const emptyResult = { contacts: [], loading: false };

    const results$ = useObservable(
        (input$) =>
            input$.pipe(
                map(([input]) => input.trim()),
                distinctUntilChanged(),
                switchMap((search) => {
                    if (search.length < DEFAULT_MIN_QUERY_LENGTH) {
                        return of(emptyResult);
                    }

                    const variables: ContactsQueryVariables = {
                        first: DEFAULT_PAGE_SIZE,
                        search,
                    };

                    return of(search).pipe(
                        debounceTime(200),
                        switchMap((debouncedSearch) =>
                            fromObservableQuery(
                                client.watchQuery<ContactsQuery, ContactsQueryVariables>({
                                    query: ContactsDocument,
                                    variables: {
                                        ...variables,
                                        search: debouncedSearch,
                                    },
                                    fetchPolicy: 'cache-first',
                                }),
                                { useInitialLoading: true }
                            )
                        ),
                        map((result) => ({
                            contacts: getFragmentData(
                                ContactFieldsFragmentDoc,
                                extractNodes(result.data?.contacts?.edges)
                            ),
                            loading: result.loading,
                        })),
                        // Clear stale suggestions immediately on input change.
                        // If we decide to keep old results visible during debounce, drop this.
                        startWith({ contacts: [], loading: true })
                    );
                }),
                catchError((error: Error) => {
                    warning(error);
                    return of(emptyResult);
                }),
                startWith(emptyResult)
            ),
        [value, client]
    );

    return useObservableState(results$, emptyResult);
}
